import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Card, Color, DeckEntry, SideboardEntry, TagProfile, HierarchicalTagProfile, BracketResponse, DirectionSuggestion, EdhrecThemeEntry } from '@mtg-deck-tool/shared';
import { computeColorIdentity, isLand } from '@mtg-deck-tool/shared';

// ─── Card Detail Modal Types ──────────────────────────────────────────────────

/** Swipe-mode data attached to a CardDetailTarget when opened from the swipe flow. */
export interface SwipeModeData {
  /** Called when the user clicks Like or presses the Like keyboard shortcut. */
  onLike: () => void;
  /** Called when the user clicks Dislike or presses the Dislike keyboard shortcut. */
  onDislike: () => void;
  /** Called when the user clicks Skip or presses the Skip keyboard shortcut. */
  onSkip: () => void;
  /** Called when the user closes the modal (X / Escape) — pauses the run. */
  onPause: () => void;
  /** "3 / 7" style progress indicator text shown at the top of the modal. */
  progressText: string;
  /** Whether to show the emerald NEW badge on this commander. */
  isNew?: boolean;
  /**
   * EDHREC strategic themes for this commander (top 5 from deal payload).
   * Shown in the "Strategic Themes" section instead of Oracle Tags / Combos.
   */
  themes: EdhrecThemeEntry[];
  /**
   * Whether EDHREC data was available for this commander at deal time.
   * When false the themes array is empty and the UI shows a pending-sync state.
   */
  hasEdhrecData: boolean;
}

export interface CardDetailTarget {
  card: Card;
  isCommander: boolean;
  discoveryMode?: boolean | undefined;
  reasons?: string[] | undefined;
  /** When present the modal renders in swipe mode. */
  swipeMode?: SwipeModeData | undefined;
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface PersistedEntry {
  cardId: string;
  cardName: string;
  quantity: number;
  isCommander: boolean;
}

interface PersistedSideboardEntry {
  cardId: string;
  cardName: string;
}

interface DeckStoreState {
  // ── Full session state ──
  entries: DeckEntry[];
  sideboard: SideboardEntry[];
  colorIdentity: Color[];
  tagProfile: TagProfile | null;
  tagHierarchy: HierarchicalTagProfile | null;
  isProfileStale: boolean;
  isProfileLoading: boolean;
  deckName: string;
  deckId: string | null;

  // ── Bracket cache ──
  bracketResult: BracketResponse | null;
  bracketCardNamesKey: string;
  isBracketLoading: boolean;
  bracketError: string | null;

  // ── DFC flip state ──
  flippedCards: Set<string>;
  toggleFlip: (oracleId: string) => void;

  // ── Directions cache (session-only, excluded from persistence) ──
  directionsCache: Map<string, DirectionSuggestion[]>;
  isDirectionsLoading: boolean;
  directionsError: string | null;
  /** Currently selected direction tag — its high synergy card names are shown in search results */
  selectedDirectionTag: DirectionSuggestion | null;

  // ── Actions ──
  addCard: (card: Card, isCommander?: boolean) => void;
  removeCard: (cardId: string) => void;
  updateQuantity: (cardId: string, qty: number) => void;
  setCommander: (cardId: string, isCommander: boolean) => void;
  importEntries: (entries: DeckEntry[]) => void;
  clearDeck: () => void;
  setDeckName: (name: string) => void;
  setDeckId: (id: string | null) => void;
  setTagProfile: (profile: TagProfile, hierarchy?: HierarchicalTagProfile) => void;
  markProfileStale: () => void;
  setProfileLoading: (loading: boolean) => void;
  setBracketResult: (result: BracketResponse, cardNamesKey: string) => void;
  setBracketLoading: (loading: boolean) => void;
  setBracketError: (error: string | null) => void;

  // ── Directions actions ──
  setDirectionsResult: (commanderKey: string, suggestions: DirectionSuggestion[]) => void;
  setDirectionsLoading: (loading: boolean) => void;
  setDirectionsError: (error: string | null) => void;
  clearDirectionsCache: () => void;
  setSelectedDirectionTag: (suggestion: DirectionSuggestion | null) => void;

  // ── Import generation (increments on each import to force re-triggers) ──
  importGeneration: number;

  // ── Moxfield source tracking ──
  moxfieldDeckId: string | null;
  setMoxfieldDeckId: (id: string | null) => void;
  isImportLoading: boolean;
  setImportLoading: (loading: boolean) => void;

  // ── Recommendations cache (survives tab switches) ──
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  recommendationsCache: Map<string, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setRecommendationsCache: (deckId: string, data: any) => void;

  // ── Bulk land operations ──
  /**
   * Add multiple cards in a single store update (one set() call).
   * If a card with the same oracleId exists, increments its quantity.
   * Otherwise appends the card as a new entry with isCommander = false.
   * Sets isProfileStale if a tag profile exists.
   */
  bulkAddCards: (cards: Array<{ card: Card; quantity: number }>) => void;

  /**
   * Remove all non-commander land entries from the deck in a single set() call.
   * Removes entries where card.typeLine contains 'Land' and isCommander === false.
   * Sets isProfileStale if a tag profile exists.
   */
  removeLandEntries: () => void;

  // ── Sideboard actions ──
  addToSideboard: (card: Card) => void;
  removeFromSideboard: (oracleId: string) => void;
  moveToDeck: (oracleId: string) => void;

  // ── Card detail modal ──
  cardDetailTarget: CardDetailTarget | null;
  openCardDetail: (card: Card, isCommander: boolean, discoveryMode?: boolean, reasons?: string[]) => void;
  closeCardDetail: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function recomputeColorIdentity(entries: DeckEntry[]): Color[] {
  const commanders = entries.filter((e) => e.isCommander);
  if (commanders.length === 0) return [];
  return computeColorIdentity(commanders.map((e) => e.card.colorIdentity));
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useDeckStore = create<DeckStoreState>()(
  persist(
    (set, get) => ({
      entries: [],
      sideboard: [],
      colorIdentity: [],
      tagProfile: null,
      tagHierarchy: null,
      isProfileStale: false,
      isProfileLoading: false,
      deckName: 'My Commander Deck',
      deckId: null,
      bracketResult: null,
      bracketCardNamesKey: '',
      isBracketLoading: false,
      bracketError: null,
      flippedCards: new Set<string>(),
      cardDetailTarget: null,
      importGeneration: 0,
      moxfieldDeckId: null,
      isImportLoading: false,
      recommendationsCache: new Map(),

      toggleFlip: (oracleId: string) => {
        set((s) => {
          const next = new Set(s.flippedCards);
          if (next.has(oracleId)) next.delete(oracleId);
          else next.add(oracleId);
          return { flippedCards: next };
        });
      },

      directionsCache: new Map(),
      isDirectionsLoading: false,
      directionsError: null,
      selectedDirectionTag: null,

      addCard: (card, isCommander = false) => {
        const { entries } = get();
        const existing = entries.find((e) => e.card.id === card.id);
        let next: DeckEntry[];
        if (existing) {
          // Commanders are singletons; non-commanders default singleton too
          next = entries.map((e) =>
            e.card.id === card.id ? { ...e, quantity: isCommander ? 1 : e.quantity + 1, isCommander: isCommander || e.isCommander } : e,
          );
        } else {
          next = [...entries, { card, quantity: 1, isCommander }];
        }
        const colorIdentity = recomputeColorIdentity(next);
        const isProfileStale = get().tagProfile !== null;
        set({ entries: next, colorIdentity, isProfileStale });
      },

      removeCard: (cardId) => {
        const next = get().entries.filter((e) => e.card.id !== cardId);
        const colorIdentity = recomputeColorIdentity(next);
        const isProfileStale = get().tagProfile !== null;
        set({ entries: next, colorIdentity, isProfileStale });
      },

      updateQuantity: (cardId, qty) => {
        if (qty <= 0) {
          get().removeCard(cardId);
          return;
        }
        const next = get().entries.map((e) =>
          e.card.id === cardId ? { ...e, quantity: qty } : e,
        );
        const isProfileStale = get().tagProfile !== null;
        set({ entries: next, isProfileStale });
      },

      setCommander: (cardId, isCommander) => {
        const next = get().entries.map((e) =>
          e.card.id === cardId ? { ...e, isCommander } : e,
        );
        const colorIdentity = recomputeColorIdentity(next);
        set({ entries: next, colorIdentity });
      },

      importEntries: (entries) => {
        const colorIdentity = recomputeColorIdentity(entries);
        set((s) => ({
          entries,
          colorIdentity,
          sideboard: [],
          tagProfile: null,
          tagHierarchy: null,
          isProfileStale: false,
          bracketResult: null,
          bracketCardNamesKey: '',
          directionsCache: new Map(),
          directionsError: null,
          selectedDirectionTag: null,
          moxfieldDeckId: null,
          recommendationsCache: new Map(),
          importGeneration: (s.importGeneration ?? 0) + 1,
        }));
      },

      clearDeck: () => {
        set({
          entries: [],
          sideboard: [],
          colorIdentity: [],
          tagProfile: null,
          isProfileStale: false,
          bracketResult: null,
          bracketCardNamesKey: '',
          bracketError: null,
          directionsCache: new Map(),
          directionsError: null,
          selectedDirectionTag: null,
          moxfieldDeckId: null,
          recommendationsCache: new Map(),
          deckId: null,
        });
      },

      setDeckName: (deckName) => set({ deckName }),
      setDeckId: (deckId) => set({ deckId }),

      setTagProfile: (profile, hierarchy) => set({ tagProfile: profile, tagHierarchy: hierarchy ?? null, isProfileStale: false }),
      markProfileStale: () => set({ isProfileStale: true }),
      setProfileLoading: (loading) => set({ isProfileLoading: loading }),

      setBracketResult: (result, cardNamesKey) => set({ bracketResult: result, bracketCardNamesKey: cardNamesKey, isBracketLoading: false, bracketError: null }),
      setBracketLoading: (loading) => set({ isBracketLoading: loading }),
      setBracketError: (error) => set({ bracketError: error, isBracketLoading: false }),

      // ── Directions actions ──
      setDirectionsResult: (commanderKey, suggestions) => {
        const next = new Map(get().directionsCache);
        next.set(commanderKey, suggestions);
        set({ directionsCache: next, isDirectionsLoading: false, directionsError: null });
      },
      setDirectionsLoading: (loading) => set({ isDirectionsLoading: loading }),
      setDirectionsError: (error) => set({ directionsError: error, isDirectionsLoading: false }),
      clearDirectionsCache: () => set({ directionsCache: new Map(), directionsError: null, selectedDirectionTag: null }),
      setSelectedDirectionTag: (suggestion) => set({ selectedDirectionTag: suggestion }),

      // ── Bulk land operations ──

      bulkAddCards: (cards) => {
        const existing = get().entries;
        const updatedEntries = [...existing];
        for (const { card, quantity } of cards) {
          const idx = updatedEntries.findIndex((e) => e.card.oracleId === card.oracleId);
          if (idx >= 0) {
            updatedEntries[idx] = { ...updatedEntries[idx]!, quantity: updatedEntries[idx]!.quantity + quantity };
          } else {
            updatedEntries.push({ card, quantity, isCommander: false });
          }
        }
        const colorIdentity = recomputeColorIdentity(updatedEntries);
        const isProfileStale = get().tagProfile !== null;
        set({ entries: updatedEntries, colorIdentity, isProfileStale });
      },

      removeLandEntries: () => {
        const next = get().entries.filter(
          (e) => !(isLand(e.card) && !e.isCommander),
        );
        const colorIdentity = recomputeColorIdentity(next);
        const isProfileStale = get().tagProfile !== null;
        set({ entries: next, colorIdentity, isProfileStale });
      },

      // ── Sideboard actions ──

      addToSideboard: (card) => {
        const { sideboard, entries } = get();
        // No-op if already in sideboard or already in deck
        if (sideboard.some((c) => c.card.oracleId === card.oracleId)) return;
        if (entries.some((e) => e.card.oracleId === card.oracleId)) return;
        set({ sideboard: [...sideboard, { card }] });
      },

      removeFromSideboard: (oracleId) => {
        const next = get().sideboard.filter((c) => c.card.oracleId !== oracleId);
        set({ sideboard: next });
      },

      setMoxfieldDeckId: (id) => set({ moxfieldDeckId: id }),
      setImportLoading: (loading) => set({ isImportLoading: loading }),
      setRecommendationsCache: (deckId, cards) => {
        const next = new Map(get().recommendationsCache);
        next.set(deckId, cards);
        set({ recommendationsCache: next });
      },

      openCardDetail: (card, isCommander, discoveryMode, reasons) => set({ cardDetailTarget: { card, isCommander, discoveryMode, reasons } }),
      closeCardDetail: () => set({ cardDetailTarget: null }),

      moveToDeck: (oracleId) => {
        const { sideboard, entries } = get();
        const entry = sideboard.find((c) => c.card.oracleId === oracleId);
        if (!entry) return;
        const nextSideboard = sideboard.filter((c) => c.card.oracleId !== oracleId);
        // If already in deck, just remove from sideboard
        if (entries.some((e) => e.card.oracleId === entry.card.oracleId)) {
          set({ sideboard: nextSideboard });
          return;
        }
        // Add to deck
        const nextEntries = [...entries, { card: entry.card, quantity: 1, isCommander: false }];
        const colorIdentity = recomputeColorIdentity(nextEntries);
        const isProfileStale = get().tagProfile !== null;
        set({ sideboard: nextSideboard, entries: nextEntries, colorIdentity, isProfileStale });
      },
    }),
    {
      name: 'mtg-deck-tool:deck',
      // Persist only minimal card data — rehydrate full cards via resolve on load
      partialize: (state) => ({
        deckId: state.deckId,
        deckName: state.deckName,
        moxfieldDeckId: state.moxfieldDeckId,
        // Store just IDs + names + quantities + commander flags
        _persisted: state.entries.map(
          (e): PersistedEntry => ({
            cardId: e.card.id,
            cardName: e.card.name,
            quantity: e.quantity,
            isCommander: e.isCommander,
          }),
        ),
        _persistedSideboard: state.sideboard.map(
          (c): PersistedSideboardEntry => ({
            cardId: c.card.id,
            cardName: c.card.name,
          }),
        ),
      }),
      // On rehydration, the full card objects are missing — the app calls
      // hydratePersistedDeck() after mounting to restore them via /api/cards/resolve
      merge: (persisted, current) => {
        const p = persisted as {
          deckId?: string | null;
          deckName?: string;
          moxfieldDeckId?: string | null;
          _persisted?: PersistedEntry[];
          _persistedSideboard?: PersistedSideboardEntry[];
          _persistedConsidering?: PersistedSideboardEntry[]; // old key — migration source
        };

        // Migrate old key to new key if new key is absent or empty
        const sideboardData = (p._persistedSideboard && p._persistedSideboard.length > 0)
          ? p._persistedSideboard
          : (p._persistedConsidering ?? []);

        return {
          ...current,
          deckId: p.deckId ?? null,
          deckName: p.deckName ?? current.deckName,
          moxfieldDeckId: p.moxfieldDeckId ?? null,
          _persistedEntries: p._persisted ?? [],
          _persistedSideboard: sideboardData,
        } as DeckStoreState & {
          deckId: string | null;
          _persistedEntries: PersistedEntry[];
          _persistedSideboard: PersistedSideboardEntry[];
        };
      },
    },
  ),
);

/** Returns the persisted entries needing rehydration (card names only). Includes sideboard cards. */
export function getPersistedCardNames(): string[] {
  const raw = localStorage.getItem('mtg-deck-tool:deck');
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as {
      state?: {
        _persisted?: PersistedEntry[];
        _persistedSideboard?: PersistedSideboardEntry[];
        _persistedConsidering?: PersistedSideboardEntry[];
      };
    };
    const deckNames = (parsed.state?._persisted ?? []).map((e) => e.cardName);
    const sideboardNames = (
      parsed.state?._persistedSideboard ??
      parsed.state?._persistedConsidering ??
      []
    ).map((e) => e.cardName);
    // Deduplicate names (a card could theoretically appear in both, though the store prevents it)
    return [...new Set([...deckNames, ...sideboardNames])];
  } catch {
    return [];
  }
}

/** Returns the full persisted entries for commander-flag restoration. */
export function getPersistedEntries(): PersistedEntry[] {
  const raw = localStorage.getItem('mtg-deck-tool:deck');
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as {
      state?: { _persisted?: PersistedEntry[] };
    };
    return parsed.state?._persisted ?? [];
  } catch {
    return [];
  }
}

/** Returns the persisted sideboard entries for rehydration. Includes migration fallback from old _persistedConsidering key. */
export function getPersistedSideboardEntries(): PersistedSideboardEntry[] {
  const raw = localStorage.getItem('mtg-deck-tool:deck');
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as {
      state?: {
        _persistedSideboard?: PersistedSideboardEntry[];
        _persistedConsidering?: PersistedSideboardEntry[];
      };
    };
    return (
      parsed.state?._persistedSideboard ??
      parsed.state?._persistedConsidering ??
      []
    );
  } catch {
    return [];
  }
}
